import {
  SET_CONTROLLERS,
  UPDATE_CONTROLLER_POINTS,
  RESET_CONTROLLERS,
  REMOVE_CONTROLLER,
  UPDATE_CONTROLLER_DETAILS,
  UPDATE_CURRENT_SITE_CONTROLLERS,
  ADD_CONTROLLER,
  RECONCILE_CONTROLLERS,
  SET_LAST_VALID_READING_AT,
  SET_LAST_VALID_POINT_VALUES,
} from "../actions/index";
import { reconcileById } from "./reconcile";
import utils from "utils";

const initialData = { byId: {}, allIds: [] };

const isValidLastValue = (v) =>
  v !== undefined && v !== null && v !== -32767 && v !== -32768;

// Merge lastValue from prevPointsById into nextPointsById where the new value is missing/sentinel.
const mergeLastValues = (prevPointsById, nextPointsById) => {
  if (!prevPointsById || !nextPointsById) return nextPointsById;
  let changed = false;
  const merged = Object.fromEntries(
    Object.entries(nextPointsById).map(([key, point]) => {
      const prevLast = prevPointsById[key]?.lastValue;
      if (isValidLastValue(prevLast) && !isValidLastValue(point.lastValue)) {
        changed = true;
        return [key, { ...point, lastValue: prevLast }];
      }
      return [key, point];
    })
  );
  return changed ? merged : nextPointsById;
};

const setControllers = (_state, payload) => payload;

const preserveEphemeral = (prev, next) => {
  let out = next;
  if (prev?.lastValidReadingAt !== undefined)
    out = { ...out, lastValidReadingAt: prev.lastValidReadingAt };
  if (prev?.noLinkFromSignalR !== undefined)
    out = { ...out, noLinkFromSignalR: prev.noLinkFromSignalR };
  if (prev?.pointsById && out.pointsById)
    out = { ...out, pointsById: mergeLastValues(prev.pointsById, out.pointsById) };
  return out;
};

const reconcileControllers = (state, payload) => {
  const result = reconcileById(state, payload);
  if (result === state) return state;
  const nextById = Object.fromEntries(
    Object.entries(result.byId).map(([id, ctrl]) => [
      id, preserveEphemeral(state.byId?.[id], ctrl),
    ])
  );
  return { ...result, byId: nextById };
};

const isPlainObject = (val) =>
  val !== null && typeof val === "object" && !Array.isArray(val);

const shallowEqualUnits = (a, b) => {
  if (a === b) return true;
  if (!isPlainObject(a) || !isPlainObject(b)) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((k) => a[k] === b[k]);
};

// The XIG (dongle) stamps its own WiFi envelope (rssi / rssiLevel) onto the
// telemetry of every device behind it. The indicator reads it off the device
// that holds the radio, so a reading streamed by an attached controller must
// propagate to the sibling dongle.
const toWifiValue = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * The WiFi envelope of a telemetry payload, as `{ level, rssi }`.
 *
 * It lives on its own field and never in `pointsById`: the envelope is the
 * gateway's radio, not a measure of the device, and every screen that lists a
 * device's points lists all of them — the single view draws a card per point,
 * the chart offers each one as a series, the alarm forms offer each one as a
 * variable. A point here would show up in all of those.
 *
 * Undefined when the payload carries nothing usable: a no-link frame says
 * nothing about the radio, and the sentinel values are not readings.
 */
const readWifiEnvelope = (incomingMap, isRealReading) => {
  if (!isRealReading) return undefined;
  const usable = (value) => {
    const n = toWifiValue(value);
    return isValidLastValue(n) ? n : undefined;
  };
  const level = usable(incomingMap.RSSILEVEL?.value);
  const rssi = usable(incomingMap.RSSI?.value);
  if (level === undefined && rssi === undefined) return undefined;
  return { level, rssi };
};

/** Merge an envelope onto a controller, keeping the reference when nothing moves. */
const withWifiEnvelope = (controller, envelope) => {
  if (!envelope) return controller;
  const prev = controller.wifi;
  const next = {
    level: envelope.level ?? prev?.level,
    rssi: envelope.rssi ?? prev?.rssi,
  };
  if (prev && prev.level === next.level && prev.rssi === next.rssi) return controller;
  return { ...controller, wifi: next };
};

const findGatewayDongle = (state, gatewayGuid, excludeGuid) => {
  if (!gatewayGuid) return undefined;
  const id = state.allIds?.find((gid) => {
    if (gid === excludeGuid) return false;
    const c = state.byId[gid];
    return c?.isDongleController && c?.gateway?.gatewayGuid === gatewayGuid;
  });
  return id ? { deviceGuid: id, controller: state.byId[id] } : undefined;
};

// Build the dongle update carrying the WiFi envelope from an attached
// controller's payload. Returns null when there is nothing to propagate: the
// source is the dongle itself, the payload has no envelope, no sibling dongle
// exists, or the level it reports is the one the dongle already shows.
const computeDongleWifiUpdate = (state, payload, srcController, envelope) => {
  if (!envelope) return null;
  if (srcController.isDongleController) return null;

  const dongle = findGatewayDongle(
    state,
    srcController.gateway?.gatewayGuid,
    payload.deviceGuid
  );
  if (!dongle?.controller) return null;

  const controller = withWifiEnvelope(dongle.controller, envelope);
  if (controller === dongle.controller) return null;
  return { deviceGuid: dongle.deviceGuid, controller };
};

const updateControllerPoints = (state, payload) => {
  if (!state?.byId) return state;
  if (!payload?.deviceGuid || !Array.isArray(payload?.points)) {
    utils.debug(
      "warn",
      `UPDATE_CONTROLLER_POINTS received invalid payload: ${JSON.stringify(payload)}`
    );
    return state;
  }

  const prevController = state.byId[payload.deviceGuid];
  if (!prevController?.pointsById) return state;

  const prevPoints = prevController.pointsById;
  const deviceUnitsPayload = isPlainObject(payload?.deviceUnits)
    ? payload.deviceUnits
    : null;
  const nextDeviceUnits = deviceUnitsPayload ?? prevController.deviceUnits;

  const isRealReading = !payload.noLink;
  const isValidDate = (d) => {
    if (!d) return false;
    const parsed = new Date(d);
    return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 1;
  };
  // The `date` field on the SignalR payload is always DateTime.MinValue (never populated
  // by the producer). The actual reading timestamp travels as a point named "dateTimeUtc".
  const dateTimeUtcPoint = isRealReading
    ? payload.points.find(
        (p) => typeof p?.name === "string" && p.name.toLowerCase() === "datetimeutc"
      )
    : null;
  const nextLastValidReadingAt =
    isRealReading && isValidDate(dateTimeUtcPoint?.value)
      ? dateTimeUtcPoint.value
      : prevController.lastValidReadingAt;

  // Build a quick lookup map from incoming points (case-insensitive by name)
  const incomingMap = payload.points.reduce((acc, n) => {
    if (!n || typeof n.name !== "string") return acc;
    acc[n.name.toUpperCase()] = n;
    return acc;
  }, {});

  let changed = false;
  const nextPointsById = Object.keys(prevPoints).reduce((acc, key) => {
    const prevPoint = prevPoints[key];
    const incoming = incomingMap[key.toUpperCase()];
    if (incoming) {
      const nextValue = incoming.value;
      const nextDate = payload.date;
      const sameValue = prevPoint && prevPoint.value === nextValue;
      const sameDate = prevPoint && prevPoint.date === nextDate;
      const nextLastValue =
        isRealReading && isValidLastValue(nextValue) ? nextValue : prevPoint?.lastValue;
      const sameLastValue = nextLastValue === prevPoint?.lastValue;
      acc[key] =
        sameValue && sameDate && sameLastValue
          ? prevPoint
          : { ...prevPoint, value: nextValue, date: nextDate, lastValue: nextLastValue };
      changed = changed || !(sameValue && sameDate && sameLastValue);
    } else {
      acc[key] = prevPoint; // untouched
    }
    return acc;
  }, {});

  // The envelope rides on the telemetry of every device behind the gateway, and
  // is kept on its own field — see readWifiEnvelope. This is what makes the
  // indicator live instead of a snapshot taken at page load.
  const envelope = readWifiEnvelope(incomingMap, isRealReading);

  const unitsChanged =
      deviceUnitsPayload && !shallowEqualUnits(prevController.deviceUnits ?? {}, deviceUnitsPayload);

  const nextNoLinkFromSignalR = !isRealReading;
  const noLinkChanged = nextNoLinkFromSignalR !== prevController.noLinkFromSignalR;
  const timestampChanged = nextLastValidReadingAt !== prevController.lastValidReadingAt;

  const dongleUpdate = computeDongleWifiUpdate(
    state,
    payload,
    prevController,
    envelope
  );

  // Only the device that holds the radio keeps the envelope: on a controller
  // behind a dongle the reading is the dongle's, and it is propagated there.
  const nextWifi =
    envelope && prevController.isDongleController
      ? withWifiEnvelope(prevController, envelope).wifi
      : prevController.wifi;
  const wifiChanged = nextWifi !== prevController.wifi;

  const mainChanged =
    changed || unitsChanged || noLinkChanged || timestampChanged || wifiChanged;
  if (!mainChanged && !dongleUpdate) return state;

  let nextById = state.byId;
  if (mainChanged) {
    nextById = {
      ...nextById,
      [payload.deviceGuid]: {
        ...prevController,
        pointsById: nextPointsById,
        deviceUnits: nextDeviceUnits,
        lastValidReadingAt: nextLastValidReadingAt,
        noLinkFromSignalR: nextNoLinkFromSignalR,
        wifi: nextWifi,
      },
    };
  }
  if (dongleUpdate) {
    nextById = { ...nextById, [dongleUpdate.deviceGuid]: dongleUpdate.controller };
  }

  return { ...state, byId: nextById };
};

const updateCurrentSiteControllers = (state, payload) => {
  const currentGatewayGuid =
    state?.byId[state?.allIds?.[0]]?.gateway?.gatewayGuid;
  if (!payload?.gatewayGuid || currentGatewayGuid !== payload.gatewayGuid) return state;
  const nextById = Object.fromEntries(
    Object.entries(payload.byId ?? {}).map(([id, ctrl]) => [
      id, preserveEphemeral(state.byId?.[id], ctrl),
    ])
  );
  return { ...payload, byId: nextById };
};

const updateControllerDetails = (state, payload) => {
  const { deviceGuid, friendlyName, cabinetSerial } = payload ?? {};
  if (!deviceGuid) return state;

  const prev = state.byId?.[deviceGuid] || {};
  return {
    ...state,
    byId: {
      ...state.byId,
      [deviceGuid]: {
        ...prev,
        friendlyName,
        cabinetSerial,
      },
    },
    allIds: state.allIds?.includes(deviceGuid)
      ? state.allIds
      : [...state.allIds, deviceGuid],
  };
};

const resetControllers = () => initialData;

const addController = (state, payload) => {
  if (!payload?.deviceGuid) return state;
  const prev = state.byId?.[payload.deviceGuid];
  const next = preserveEphemeral(prev, payload);
  return {
    ...state,
    byId: { ...state.byId, [payload.deviceGuid]: next },
    allIds: state.allIds.includes(payload.deviceGuid)
      ? state.allIds
      : [...state.allIds, payload.deviceGuid],
  };
};

const setLastValidReadingAt = (state, payload) => {
  const { deviceGuid, lastValidReadingAt } = payload ?? {};
  if (!deviceGuid || !lastValidReadingAt) return state;
  const prev = state.byId?.[deviceGuid];
  if (!prev) return state;
  // Only set the initial value — don't overwrite a more recent one from SignalR
  if (prev.lastValidReadingAt !== undefined) return state;
  return {
    ...state,
    byId: { ...state.byId, [deviceGuid]: { ...prev, lastValidReadingAt } },
  };
};

const setLastValidPointValues = (state, payload) => {
  const { deviceGuid, values } = payload ?? {};
  if (!deviceGuid || !values) return state;
  const prev = state.byId?.[deviceGuid];
  if (!prev?.pointsById) return state;

  let changed = false;
  const nextPointsById = Object.fromEntries(
    Object.entries(prev.pointsById).map(([key, point]) => {
      if (isValidLastValue(point.lastValue)) return [key, point];
      const cachedValue = values[key];
      if (!isValidLastValue(cachedValue)) return [key, point];
      changed = true;
      return [key, { ...point, lastValue: cachedValue }];
    })
  );

  if (!changed) return state;
  return {
    ...state,
    byId: { ...state.byId, [deviceGuid]: { ...prev, pointsById: nextPointsById } },
  };
};

const removeController = (state, deviceGuid) => {
  if (!deviceGuid || !state.byId[deviceGuid]) return state;
  const byId = { ...state.byId };
  delete byId[deviceGuid];
  return { ...state, byId, allIds: state.allIds.filter((id) => id !== deviceGuid) };
};

const handlers = {
  [SET_CONTROLLERS]: setControllers,
  [RECONCILE_CONTROLLERS]: reconcileControllers,
  [UPDATE_CONTROLLER_POINTS]: updateControllerPoints,
  [UPDATE_CURRENT_SITE_CONTROLLERS]: updateCurrentSiteControllers,
  [UPDATE_CONTROLLER_DETAILS]: updateControllerDetails,
  [RESET_CONTROLLERS]: resetControllers,
  [REMOVE_CONTROLLER]: removeController,
  [ADD_CONTROLLER]: addController,
  [SET_LAST_VALID_READING_AT]: setLastValidReadingAt,
  [SET_LAST_VALID_POINT_VALUES]: setLastValidPointValues,
};

const controllers = (state = initialData, action) => {
  const handler = handlers[action.type];
  return handler ? handler(state, action.payload) : state;
};

export default controllers;
